import { useEffect, useState } from 'react';
import {
  MAX_INTERVAL_SECONDS,
  MIN_INTERVAL_SECONDS,
  formatInterval,
  joinInterval,
  splitInterval,
} from '../../utils/interval';
import { cn } from '../../utils/cn';

interface IntervalFieldProps {
  id?: string;
  value: number;
  onChange: (seconds: number) => void;
  disabled?: boolean;
}

type Unit = 'minutes' | 'hours' | 'days';

const PRESETS = [300, 900, 1800, 3600, 21_600, 86_400];

export function intervalError(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Enter how often it runs.';
  if (seconds < MIN_INTERVAL_SECONDS) return 'Run it at most once a minute.';
  if (seconds > MAX_INTERVAL_SECONDS) return 'Run it at least once every 90 days.';
  return null;
}

/** "96 runs a day", "1 run every 2 days": the number people actually weigh an interval by. */
function cadence(seconds: number): string {
  if (seconds <= 86_400) {
    const perDay = 86_400 / seconds;
    const rounded = Number.isInteger(perDay) ? perDay : Math.round(perDay * 10) / 10;
    return `${rounded} run${rounded === 1 ? '' : 's'} a day`;
  }
  return `1 run every ${formatInterval(seconds)}`;
}

/**
 * How often a schedule runs: a number and a unit, with the common cadences one
 * click away. The bot spaces schedules out itself, so this is only ever "how
 * often", never "at what minute".
 */
export const IntervalField = ({ id, value, onChange, disabled = false }: IntervalFieldProps) => {
  const initial = splitInterval(value);
  const [amount, setAmount] = useState(String(initial.value));
  const [unit, setUnit] = useState<Unit>(initial.unit);

  // Follow changes made from outside (a preset, a reset, a loaded schedule)
  // without fighting the person typing: only resync when the numbers disagree.
  useEffect(() => {
    const typed = Number(amount);
    if (Number.isFinite(typed) && joinInterval(typed, unit) === value) return;
    const next = splitInterval(value);
    setAmount(String(next.value));
    setUnit(next.unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (nextAmount: string, nextUnit: Unit) => {
    setAmount(nextAmount);
    setUnit(nextUnit);
    const typed = Number(nextAmount);
    if (nextAmount.trim() !== '' && Number.isFinite(typed) && typed > 0) onChange(joinInterval(typed, nextUnit));
    else onChange(0);
  };

  const error = intervalError(value);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-glass-300">Every</span>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          className={cn('glass-input w-24 tabular-nums', error && 'border-red-500/50')}
          value={amount}
          onChange={(e) => commit(e.target.value, unit)}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={id ? `${id}-help` : undefined}
        />
        <select
          className="glass-select"
          value={unit}
          onChange={(e) => commit(amount, e.target.value as Unit)}
          disabled={disabled}
          aria-label="Unit"
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Common intervals">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset)}
              disabled={disabled}
              className={cn('btn-xs glass-button-secondary tabular-nums', value === preset && 'border-accent-400/50 text-accent-300')}
              aria-pressed={value === preset}
            >
              {formatInterval(preset)}
            </button>
          ))}
        </div>
      </div>
      <p id={id ? `${id}-help` : undefined} className={cn('text-xs', error ? 'text-red-400' : 'text-glass-500')}>
        {error ?? `${cadence(value)}. Mr. Roboto picks the exact minute so schedules do not land on top of each other.`}
      </p>
    </div>
  );
};
